/**
 * Config-driven admin dashboard: renders primary items as cards, optionally plus Advanced Settings.
 * When config.admin.primaryItems is set, shows only those items + Advanced link.
 * When omitted, shows full admin menu as cards.
 */
import Link from 'next/link'
import type { ReactNode } from 'react'

import type { ShellConfig } from '../../config/shell-config'
import { getShellAdminMenuItems } from '../../pages/admin/menu-items'

const ADVANCED_HREF = '/admin/settings'
const ADVANCED_LABEL_DEFAULT = 'Advanced Settings'
const ADVANCED_ICON = 'ri-settings-3-line'

export type AdminDashboardSlots = {
  header?: ReactNode
  extraCards?: ReactNode
  footer?: ReactNode
}

type AdminDashboardPageProps = {
  config: ShellConfig
  /** Optional slots for injecting content */
  slots?: AdminDashboardSlots
}

function AdminCard({
  href,
  label,
  description,
  icon,
}: {
  href: string
  label: string
  description?: string
  icon?: string
}) {
  return (
    <Link
      href={href}
      className="rounded-lg border korvo-bg-background-secondary p-4 shadow-subtle hover:shadow-md transition-all group block korvo-border-border"
    >
      <div className="flex items-center gap-4">
        {icon != null && (
          <div
            className="p-3 rounded-lg bg-primary-default/10 group-hover:bg-primary-default/20 transition-colors"
            style={{ color: 'var(--primary-default)' }}
          >
            <i className={`${icon} text-2xl`} aria-hidden />
          </div>
        )}
        <div>
          <h3 className="font-semibold text-foreground-default">{label}</h3>
          {description != null && (
            <p className="text-sm text-foreground-muted">{description}</p>
          )}
        </div>
      </div>
    </Link>
  )
}

export function AdminDashboardPage({ config, slots }: AdminDashboardPageProps) {
  const adminConfig = config.admin
  const primaryItems = adminConfig?.primaryItems
  const advancedLabel = adminConfig?.advancedLabel ?? ADVANCED_LABEL_DEFAULT
  const showAdvanced = primaryItems != null && advancedLabel !== false

  const items =
    primaryItems != null && primaryItems.length > 0
      ? primaryItems
      : getShellAdminMenuItems()

  return (
    <div className="space-y-4">
      {slots?.header}
      <div className="rounded-lg border korvo-bg-background-secondary korvo-border-border p-4 md:p-5 shadow-subtle">
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--primary-default)' }}>
            Admin
          </p>
          <h1 className="text-2xl font-bold text-foreground-default md:text-3xl">Dashboard</h1>
          <p className="text-foreground-muted mt-1 text-sm md:text-base">
            {adminConfig?.subtitle ?? 'Overview and quick access to admin features'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((item) => (
          <AdminCard
            key={item.id}
            href={item.href}
            label={item.label}
            description={item.description}
            icon={item.icon}
          />
        ))}
        {showAdvanced && (
          <AdminCard
            href={ADVANCED_HREF}
            label={typeof advancedLabel === 'string' ? advancedLabel : ADVANCED_LABEL_DEFAULT}
            description="System configuration"
            icon={ADVANCED_ICON}
          />
        )}
        {slots?.extraCards}
      </div>
      {slots?.footer}
    </div>
  )
}
